import React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Typography
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import WarningIcon from '@mui/icons-material/Warning';
import { useTranslation } from 'react-i18next';

/**
 * CaseDeleteDialog Component
 * 
 * A reusable confirmation dialog for deleting cases/projects.
 * Follows DRY principle by centralizing delete confirmation UI and behavior.
 * 
 * Features:
 * - Customizable title and message
 * - Warning styling (red theme)
 * - Internationalization support
 * - Accessible ARIA labels
 * - Optional detailed description
 * 
 * @param {boolean} open - Whether the dialog is open
 * @param {function} onClose - Handler for closing the dialog
 * @param {function} onConfirm - Handler for confirming deletion
 * @param {string} caseTitle - Optional title of the case being deleted (for context)
 * @param {string} title - Optional custom dialog title (defaults to translation key)
 * @param {string} message - Optional custom message (defaults to translation key)
 * @param {string} description - Optional additional description
 * @param {boolean} showDetailedWarning - Whether to show Settings-style detailed warning (default: false)
 */
const CaseDeleteDialog = ({
  open,
  onClose,
  onConfirm,
  caseTitle = null,
  title = null,
  message = null,
  description = null,
  showDetailedWarning = false,
  isDeleting = false
}) => {
  const { t } = useTranslation();

  // Determine which translation keys to use based on context
  const dialogTitle = title || (showDetailedWarning 
    ? t('settings.delete_confirm_title') 
    : t('sidebar.delete_confirm_title') || 'Delete Case?'
  );

  const dialogMessage = message || (showDetailedWarning
    ? null // Settings uses separate warning and description
    : caseTitle 
      ? (t('sidebar.delete_confirm_message', { title: caseTitle }) || 
         `Are you sure you want to permanently delete "${caseTitle}"? This action cannot be undone.`)
      : (t('sidebar.delete_confirm_message_generic') || 
         'Are you sure you want to permanently delete this case? This action cannot be undone.')
  );

  const dialogDescription = description || (showDetailedWarning
    ? t('settings.delete_confirm_description')
    : null
  );

  const warningText = showDetailedWarning ? t('settings.delete_confirm_warning') : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="delete-dialog-title"
      aria-describedby="delete-dialog-description"
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: 10,
        }
      }}
    >
      <DialogTitle 
        id="delete-dialog-title" 
        sx={{ 
          bgcolor: '#ff6666', 
          color: 'white', 
          display: 'flex', 
          alignItems: 'center' 
        }}
      >
        <WarningIcon sx={{ mr: 1 }} /> {dialogTitle}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="delete-dialog-description" sx={{ mt: 2 }}>
          {showDetailedWarning && warningText && (
            <Typography variant="body1" fontWeight="bold" paragraph>
              {warningText}
            </Typography>
          )}
          {dialogMessage && (
            <Typography variant="body2" paragraph={!!dialogDescription}>
              {dialogMessage}
            </Typography>
          )}
          {dialogDescription && (
            <Typography variant="body2" paragraph>
              {dialogDescription}
            </Typography>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button onClick={onClose} variant="outlined">
          {t('sidebar.delete_confirm_cancel') || t('settings.delete_confirm_cancel') || 'Cancel'}
        </Button>
        <Button 
          onClick={onConfirm} 
          variant="contained" 
          color="error"
          disabled={isDeleting}
          sx={{
            backgroundColor: '#ff6666',
            '&:hover': { backgroundColor: '#ff9999' },
          }}
        >
          {isDeleting ? (
            <>
              <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
              {t('sidebar.delete_confirm_deleting') || t('settings.delete_confirm_deleting') || 'Deleting...'}
            </>
          ) : (
            (t('sidebar.delete_confirm_proceed') || t('settings.delete_confirm_proceed') || 'Delete')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CaseDeleteDialog;
